/**
 * Project Memory endpoints — project detail + lists of notes / tasks /
 * decisions / risks for a given project.
 *
 * Mirrors `src/aimemory/api/routers/projects.py` and `project_memory.py`.
 * All requests carry the Bearer JWT injected by `request` in `./client.ts`.
 */
import type {
  Decision,
  Note,
  ProjectDetail,
  Risk,
  Task,
  TaskStatus,
} from "../lib/types";
import { request } from "./client";

export interface PaginationParams {
  limit?: number;
  offset?: number;
}

export interface ListTasksParams extends PaginationParams {
  status?: TaskStatus;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

export function getProjectDetail(
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectDetail> {
  return request<ProjectDetail>(
    `/api/v1/projects/${encodeURIComponent(projectId)}`,
    signal ? { signal } : {},
  );
}

export function listNotes(
  projectId: string,
  params: PaginationParams = {},
  signal?: AbortSignal,
): Promise<Note[]> {
  const qs = buildQuery({
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  });
  return request<Note[]>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/notes${qs}`,
    signal ? { signal } : {},
  );
}

export function listTasks(
  projectId: string,
  params: ListTasksParams = {},
  signal?: AbortSignal,
): Promise<Task[]> {
  const qs = buildQuery({
    status: params.status,
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
  });
  return request<Task[]>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/tasks${qs}`,
    signal ? { signal } : {},
  );
}

export function listDecisions(
  projectId: string,
  signal?: AbortSignal,
): Promise<Decision[]> {
  return request<Decision[]>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/decisions`,
    signal ? { signal } : {},
  );
}

export function listRisks(
  projectId: string,
  signal?: AbortSignal,
): Promise<Risk[]> {
  return request<Risk[]>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/risks`,
    signal ? { signal } : {},
  );
}
